import checkAndCircleWhite from 'img/check-and-circle-white.svg'
import plus from 'img/plus.svg'
import rightTriangleWhite from 'img/right-triangle-white.svg'
import xAndCircleWhite from 'img/x-and-circle-white.svg'
import groupsIcon from 'img/dashicons-groups.svg'
import copyIcon from 'img/dashicons-admin-page.svg'
import editPageIcon from 'img/dashicons-edit-page.svg'
import downloadIcon from 'img/dashicons-download.svg'
import styles from 'css/components/lesson-builder/lesson-status.module.css'
import ActionDropdown from 'components/action-dropdown'
import React, { useState, useRef, useEffect } from 'react'

interface LessonReadyRowProps {
	allApproved: boolean
	unapprovedCount: number
	teachStatus: string
	isClassAssigned: boolean
	assignedClassName?: string
	onAssignClass: () => void
	onLaunchLesson: () => void
}

interface LessonOptionsDropdownProps {
	isClassAssigned: boolean
	assignedClassName?: string
	lessonStatus: string
	onShowStudentLogins: () => void
	onCopyToClass: () => void
	onPrintDraft: () => void
	onPrintAnswerKey: () => void
	onPrintWorksheets: () => void
}

const ReadyStatus = ({ isReady, icon, text }: { isReady: boolean, icon: string, text: string }) => (
	<div className={`${isReady ? 'ready' : 'not-ready'} status`}>
		<img src={icon} alt={`${text} icon`} />
		{text}
	</div>
);

export const LessonReadyRow = ({
	allApproved,
	unapprovedCount,
	teachStatus,
	isClassAssigned,
	assignedClassName,
	onAssignClass,
	onLaunchLesson
}: LessonReadyRowProps) => {
	const hasClass = isClassAssigned || !!assignedClassName

	if (!allApproved) {
		/* The mockup styles this as a bordered pill with a terracotta count
		   chip (.pending-badge / .pending-badge .count), not plain text. Its
		   show-one-or-the-other behaviour matches ours already: it toggles
		   [hidden] between .pending-badge and the "Lesson:" label, per the
		   comment in its own stylesheet. */
		return (
			<div className="ready-row">
				<span className="pending-badge">
					Questions needing approval
					<span className="count">{unapprovedCount}</span>
				</span>
			</div>
		);
	}

	return (
		<div className="ready-row">
			Lesson: <ReadyStatus isReady={true} icon={checkAndCircleWhite} text="READY" />
			{hasClass || teachStatus === 'Ready' ? (
				<div className="launchLesson" onClick={onLaunchLesson}>
					Launch Lesson
					<img src={rightTriangleWhite} alt="Right Triangle White" />
				</div>
			) : (
				<>
					Teach: <ReadyStatus isReady={false} icon={xAndCircleWhite} text="NOT READY" />
					<button onClick={onAssignClass}>
						<img src={plus} alt="Plus" />
						Assign Class
					</button>
				</>
			)}
		</div>
	);
};

export const LessonOptionsDropdown = ({
	isClassAssigned,
	assignedClassName,
	lessonStatus,
	onShowStudentLogins,
	onCopyToClass,
	onPrintDraft,
	onPrintAnswerKey,
	onPrintWorksheets
}: LessonOptionsDropdownProps) => {
	const hasClass = isClassAssigned || !!assignedClassName
	const [open, setOpen] = useState(false)
	const ref = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		if (open) document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [open])

	if (!hasClass) return null;

	const actions: { label: string; onClick: () => void; icon: React.ReactNode }[] = [
		{
			label: 'Student Logins',
			onClick: () => { onShowStudentLogins(); setOpen(false) },
			icon: <img src={groupsIcon} alt="Student Logins" />
		},
		{
			label: 'Copy to New Class',
			onClick: () => { onCopyToClass(); setOpen(false) },
			icon: <img src={copyIcon} alt="Copy to New Class" />
		},
	]

	if (lessonStatus === 'Draft') {
		actions.push({
			label: 'Download Draft Questions',
			onClick: () => { onPrintDraft(); setOpen(false) },
			icon: <img src={editPageIcon} alt="Download Draft" />
		})
	}

	if (lessonStatus === 'Ready') {
		actions.push({
			label: 'Download Answer Key',
			onClick: () => { onPrintAnswerKey(); setOpen(false) },
			icon: <img src={downloadIcon} alt="Download Answer Key" />
		})
		actions.push({
			label: 'Download Worksheets',
			onClick: () => { onPrintWorksheets(); setOpen(false) },
			icon: <img src={editPageIcon} alt="Download Worksheets" />
		})
	}

	return (
		<div className={styles.optionsContainer} ref={ref}>
			<button
				className={styles.actionButton}
				onClick={() => setOpen(!open)}
				aria-expanded={open}
				aria-haspopup="menu"
			>
				Lesson Options
				{/* Her button carries a 13x13 chevron (AllForOne_LessonGenerator.html's
				    `.btn-lesson-options`), inlined so it takes the button's own colour
				    through the hover and open states rather than shipping an asset per
				    state. aria-expanded both drives the open styling and tells a screen
				    reader the menu's state, which the plain button never did. */}
				<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
					<polyline points="6 9 12 15 18 9" />
				</svg>
			</button>
			{open && (
				<ActionDropdown
					actions={actions}
					className={styles.optionsDropdown}
				/>
			)}
		</div>
	);
};
